import { useLazyQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { Title } from 'react-admin';
import { Controller, useForm } from 'react-hook-form';
import { AdminGetUserFeatureFlagStateDocument } from '../../../graphql/types';
import { getGraphQLErrorDetails } from '../../../utils/getGraphQLErrorDetails';
import { ResourceLink } from '../../reusable/ResourceLink';
import { FeatureFlagResult } from './FeatureFlagResult';

interface FormFields {
  userIdentifier: string;
  flagKey: string;
}

interface FeatureFlagStateContentProps {
  prefilledUserId?: string;
  prefilledUserName?: string;
  showTitle?: boolean;
  showUserInfo?: boolean;
}

export const FeatureFlagStateContent: React.FC<
  FeatureFlagStateContentProps
> = ({
  prefilledUserId,
  prefilledUserName,
  showTitle = true,
  showUserInfo = true,
}) => {
  const [
    queryFlag,
    { data: flagData, loading, error: flagQueryError, called },
  ] = useLazyQuery(AdminGetUserFeatureFlagStateDocument, {
    errorPolicy: 'all',
  });

  const methods = useForm<FormFields>({
    defaultValues: {
      userIdentifier: prefilledUserId || '',
      flagKey: '',
    },
  });

  const handleSubmit = async (values: FormFields) => {
    const flagKey = values.flagKey.trim();
    const userIdentifier = prefilledUserId || values.userIdentifier?.trim();

    const isEmail = userIdentifier.includes('@');

    await queryFlag({
      variables: {
        ...(isEmail ? { email: userIdentifier } : { userId: userIdentifier }),
        key: flagKey,
      },
    });
  };

  const user = flagData?.user;
  const { appFeatureFlagState, chFeatureFlagState } = user || {};
  const userId = user?.id ?? prefilledUserId ?? null;
  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'
    : (prefilledUserName ?? null);

  const { errorRequestId, errorPath } = getGraphQLErrorDetails(flagQueryError);
  const userNotFound =
    !loading && !flagData?.user && !prefilledUserId && called;

  return (
    <Box mt={2}>
      {showTitle && (
        <>
          <Title title="Feature Flag Status" />
          <Typography variant="h3">Feature Flag Status</Typography>
        </>
      )}
      <Paper sx={{ marginTop: '1rem', padding: '1rem' }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {prefilledUserId
            ? "Enter a feature flag key to check the member's feature flag state."
            : "Enter a user email or ID and feature flag key to check the member's feature flag state."}
        </Typography>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(handleSubmit)}
          display="flex"
          flexDirection="column"
          alignItems="start"
          width={prefilledUserId ? '100%' : '40%'}
          gap={2}
          sx={{ mb: 2 }}
        >
          {!prefilledUserId && (
            <Controller
              control={methods.control}
              name="userIdentifier"
              rules={{ required: 'User email or ID is required' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="User Email or ID"
                  placeholder="user@example.com or user-id"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            <Controller
              control={methods.control}
              name="flagKey"
              rules={{ required: 'Feature flag key is required' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Feature Flag Key"
                  placeholder="e.g., my-feature-flag"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={loading}
                />
              )}
            />
            <Button
              variant="contained"
              type="submit"
              disabled={
                loading || (prefilledUserId ? !methods.watch('flagKey') : false)
              }
              sx={{ mt: 1 }}
            >
              {loading ? 'Loading...' : 'Submit'}
            </Button>
          </Box>
        </Box>

        <Box>
          {(flagQueryError || userNotFound) && (
            <Box sx={{ mb: 2 }}>
              <Typography color="error" variant="subtitle2">
                An error occurred while fetching feature flag status
              </Typography>
              {errorPath && (
                <Typography color="error" fontSize={12}>
                  Path: {errorPath}
                </Typography>
              )}
              {errorRequestId && (
                <Typography color="error" fontSize={12}>
                  Request Id: {errorRequestId}
                </Typography>
              )}
              {flagQueryError?.message && (
                <Typography color="error" fontSize={12}>
                  {flagQueryError.message || 'Unknown error'}
                </Typography>
              )}
              {userNotFound && (
                <Typography color="error" fontSize={12}>
                  User not found
                </Typography>
              )}
            </Box>
          )}

          {showUserInfo && userId && userName && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4" gutterBottom>
                User Information
              </Typography>
              <Typography>
                <strong>User:</strong>{' '}
                <ResourceLink name="User" id={userId} type="show">
                  {userName}
                </ResourceLink>
              </Typography>
              <Typography>
                <strong>User ID:</strong> {userId}
              </Typography>
            </Box>
          )}

          <Box display="flex" flexDirection="row" gap={2}>
            {chFeatureFlagState && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
                p={1}
                flexBasis={'50%'}
              >
                <Typography variant="h5" gutterBottom>
                  CH Feature Flag Status
                </Typography>
                <FeatureFlagResult featureFlagState={chFeatureFlagState} />
              </Box>
            )}
            {appFeatureFlagState && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
                p={1}
                flexBasis={'50%'}
              >
                <Typography variant="h5" gutterBottom>
                  App Feature Flag Status
                </Typography>

                <FeatureFlagResult featureFlagState={appFeatureFlagState} />
                <Alert severity="warning" sx={{ my: 1 }}>
                  <Typography variant="body2">
                    Note: These results don't accurately reflect app-specific
                    attributes like app version, OS, deviceId, etc.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export const FeatureFlagState = () => {
  return <FeatureFlagStateContent />;
};
